// Packages:
import React, { useRef, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Position } from 'react-flow-renderer';
import hasNodeMoved from '../../../../lib/hasNodeMoved';

// Redux:
import { setSelectedNode } from '../../../../redux/actions/studioActions';

// Styles:
import {
  EmailNodeWrapper,
  EmailNodeLabel,
  Body,
  Description,
  Field,
  StyledHandle
} from './styles';

// Functions:
const EmailNode = ({ data }) => {
  // Constants:
  const dispatch = useDispatch();
  const { id, from, subject, body, label } = data;

  // Ref:
  const wrapperRef = useRef(null);

  // State:
  const studio = useSelector((state) => state.studio);
  const [clickOrigin, setClickOrigin] = useState({ x: 0, y: 0 });

  // Functions:
  const handleMouseDown = useCallback(
    (e) => {
      if (e.button === 2) e.stopPropagation();
      if (e.button === 0) {
        setClickOrigin({ x: e.clientX, y: e.clientY });
        dispatch(setSelectedNode(data));
      }
    },
    [data, dispatch]
  );

  const handleMouseUp = useCallback(
    (e) => {
      if (e.button === 0) {
        if (hasNodeMoved(clickOrigin, e)) return;
      }
    },
    [clickOrigin]
  );

  // Effects:
  useEffect(() => {
    wrapperRef.current.addEventListener('mousedown', handleMouseDown);
    wrapperRef.current.addEventListener('mouseup', handleMouseUp);
  }, [handleMouseDown, handleMouseUp, clickOrigin, studio.selectedNode]);

  // Return:
  return (
    <EmailNodeWrapper
      ref={wrapperRef}
      style={{
        backgroundColor: studio.selectedNode?.id === id ? '#D43558' : '#FFFFFF',
        borderColor: '#D43558'
      }}
    >
      <StyledHandle
        type="target"
        position={Position.Top}
        style={{ backgroundColor: 'blue', top: '-15px' }}
      />
      <StyledHandle
        id="api-node-output-true"
        type="source"
        position={Position.Bottom}
        style={{
          backgroundColor: 'green',
          bottom: '-15px',
          left: '25%'
        }}
      />
      <StyledHandle
        id="api-node-output-false"
        type="source"
        position={Position.Bottom}
        style={{
          backgroundColor: '#ED1C24',
          bottom: '-15px',
          left: '75%'
        }}
      />
      <EmailNodeLabel
        htmlFor="text"
        style={{
          color: studio.selectedNode?.id === id ? '#FFFFFF' : '#333533'
        }}
      >
        {label}
      </EmailNodeLabel>
      <Body
        style={{
          color: studio.selectedNode?.id === id ? '#FFFFFF' : '#333533'
        }}
      >
        <Description>
          <Field isActive={studio.selectedNode?.id === id}>
            {from.length === 0 ? (
              <span style={{ fontStyle: 'italic', fontWeight: 400 }}>
                recipient@readyly.com
              </span>
            ) : (
              from
            )}
          </Field>
        </Description>
        <Description>
          <Field isActive={studio.selectedNode?.id === id}>
            {subject.length === 0 ? (
              <span style={{ fontStyle: 'italic', fontWeight: 400 }}>
                Enter a subject...
              </span>
            ) : subject.length >= 30 ? (
              `${subject.slice(0, 20).trim()}..`
            ) : (
              subject
            )}
          </Field>
        </Description>
        <Description>
          <Field isActive={studio.selectedNode?.id === id}>
            {body.length === 0 ? (
              <span style={{ fontStyle: 'italic', fontWeight: 400 }}>
                Body of the email...
              </span>
            ) : body.length >= 30 ? (
              `${body.slice(0, 25).trim()}..`
            ) : (
              body
            )}
          </Field>
        </Description>
      </Body>
    </EmailNodeWrapper>
  );
};

// Exports:
export default EmailNode;
